import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getApplicationData, updateApplicationCurrentFlow } from 'thunks';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { RoutePath } from 'enums/Routes';
import { ApplicationStepName } from 'enums/ApplicationStepName';
import { getEnvironment, Environments } from 'utils/getEnvironment';
import { CurrentFlow } from 'enums/CurrentFlow';

import { GetApplicationData } from './applicationData';
import { isManualReviewKycStatus, KycStatus } from './kyc';

export const VERIFICATION_PENDING_STATUSES = Object.freeze([
  ApplicationStatusName.ManualVerify,
  ApplicationStatusName.FinalReview,
]);

type StatusPathMap = Partial<Record<ApplicationStatusName, RoutePath>>;

const SHARED_STATUS_PATHS: StatusPathMap = {
  [ApplicationStatusName.Verification]: RoutePath.SetupAccount,
  [ApplicationStatusName.SetUpDeduction]: RoutePath.RepaymentMethod,
  [ApplicationStatusName.SigningAgreement]: RoutePath.DocuSignSignature,
  [ApplicationStatusName.AwaitingFunding]: RoutePath.FundsSent,
  [ApplicationStatusName.Funded]: RoutePath.FundsSent,
};

const DC_STATUS_PATHS: StatusPathMap = {
  ...SHARED_STATUS_PATHS,
  [ApplicationStatusName.OfferAvailable]: RoutePath.OfferAvailable,
  [ApplicationStatusName.ManualVerify]: RoutePath.OfferStatus,
  [ApplicationStatusName.FinalReview]: RoutePath.OfferStatus,
  [ApplicationStatusName.OutOfState]: RoutePath.OutOfState,
  [ApplicationStatusName.AlreadyApplied]: RoutePath.OfferStatus,
  [ApplicationStatusName.Rejected]: RoutePath.OfferStatus,
  [ApplicationStatusName.FinalizeLoan]: RoutePath.SelectDebts,
};

const FC_STATUS_PATHS: StatusPathMap = {
  ...SHARED_STATUS_PATHS,
  [ApplicationStatusName.ManualReview]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.OfferAvailable]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.ManualVerify]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.FinalReview]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.OutOfState]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.AlreadyApplied]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.Rejected]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.FinalizeLoan]: RoutePath.FinancialDashboard,
};

const AI_COACH_STATUS_PATHS: StatusPathMap = {
  ...DC_STATUS_PATHS,
  [ApplicationStatusName.ManualReview]: RoutePath.FinancialDashboard,
};

const getPathByStatus = (status: ApplicationStatusName, map: StatusPathMap): RoutePath => map[status] as RoutePath;

type StatusRoutesMap = Partial<Record<ApplicationStatusName, RoutePath[]>>;

// Routes a user is allowed to be on for a given application status. This is used by the
// browser back/forward guard to detect when the user landed (via POP navigation) on a route
// that belongs to a status they've already moved past, and redirect them to the correct route.
// Only statuses listed here are guarded; a status left out is treated as "anything allowed".
const SHARED_STATUS_ALLOWED_ROUTES: StatusRoutesMap = {
  [ApplicationStatusName.Verification]: [
    RoutePath.SetupAccount,
    RoutePath.VerificationSteps,
    RoutePath.VerifyingApplication,
    RoutePath.Alloy,
    RoutePath.UploadIncomeVerification,
    RoutePath.VerifyIncome,
  ],
  [ApplicationStatusName.SetUpDeduction]: [
    RoutePath.RepaymentMethod,
    RoutePath.OpenDDA,
    RoutePath.SetupDeduction,
    RoutePath.PaystubsSignInGuide,
    RoutePath.SetupViaPayroll,
    RoutePath.WhatIsTheIssue,
    RoutePath.NotifyYourTeam,
    RoutePath.VerifyPayrollDeduction,
  ],
  [ApplicationStatusName.SigningAgreement]: [RoutePath.DocuSignSignature, RoutePath.FundsSent],
  [ApplicationStatusName.AwaitingFunding]: [RoutePath.FundsSent],
  [ApplicationStatusName.Funded]: [RoutePath.FundsSent],
};

const DC_STATUS_ALLOWED_ROUTES: StatusRoutesMap = {
  ...SHARED_STATUS_ALLOWED_ROUTES,
  [ApplicationStatusName.OfferAvailable]: [
    RoutePath.OfferAvailable,
    RoutePath.ConfirmLoan,
    RoutePath.AdditionalFunds,
    RoutePath.AnalyzingUpdatedOffer,
  ],
  [ApplicationStatusName.FinalizeLoan]: [
    RoutePath.SelectDebts,
    RoutePath.FinalizeAdditionalFunds,
    RoutePath.FinalizeReviewOffer,
  ],
  [ApplicationStatusName.ManualReview]: [
    RoutePath.OfferStatus,
    RoutePath.UnfreezeCreditScore,
    RoutePath.BureauVerification,
    RoutePath.ExtendedIdentityCheck,
    RoutePath.ExtendedIdentityAddresses,
  ],
  [ApplicationStatusName.ManualVerify]: [RoutePath.OfferStatus, RoutePath.VerifyingKyc, RoutePath.Alloy],
  [ApplicationStatusName.FinalReview]: [RoutePath.OfferStatus],
  [ApplicationStatusName.AlreadyApplied]: [RoutePath.OfferStatus],
  [ApplicationStatusName.Rejected]: [RoutePath.OfferStatus],
  [ApplicationStatusName.OutOfState]: [RoutePath.OutOfState],
};

const AI_COACH_STATUS_ALLOWED_ROUTES: StatusRoutesMap = {
  [ApplicationStatusName.OfferAvailable]: [
    RoutePath.OfferStatus,
    RoutePath.AICoachGoals,
    RoutePath.AICoachMonthlySavings,
    RoutePath.PlaidCheckingAccounts,
    RoutePath.OfferAvailable,
  ],
};

const FC_STATUS_ALLOWED_ROUTES: StatusRoutesMap = AI_COACH_STATUS_ALLOWED_ROUTES;

export const STATUS_ALLOWED_ROUTES: Partial<Record<CurrentFlow, StatusRoutesMap>> = {
  [CurrentFlow.DebtConsolidation]: DC_STATUS_ALLOWED_ROUTES,
  [CurrentFlow.FinancialCheckup]: FC_STATUS_ALLOWED_ROUTES,
  [CurrentFlow.AICoach]: AI_COACH_STATUS_ALLOWED_ROUTES,
};

const FINANCIAL_DASHBOARD_ROUTES: RoutePath[] = [
  RoutePath.FinancialDashboard,
  RoutePath.YourLoans,
  RoutePath.ManageYourLoan,
  RoutePath.LoansHistory,
  RoutePath.LoanPayment,
  RoutePath.MethodPayment,
  RoutePath.AvailableShifts,
  RoutePath.ShiftDetails,
];

export const getAllowedRoutesForStatus = (
  flow: CurrentFlow,
  status: ApplicationStatusName,
): RoutePath[] | undefined => {
  const allowedRoutes = STATUS_ALLOWED_ROUTES[flow]?.[status];

  return allowedRoutes && [...allowedRoutes, ...FINANCIAL_DASHBOARD_ROUTES];
};

export const FLOW_TO_STEP_MAP: Record<
  CurrentFlow,
  (application?: GetApplicationData, completedCustomize?: boolean) => { path: RoutePath }
> = {
  [CurrentFlow.DebtConsolidation]: (application?: GetApplicationData) => {
    if (!application?.status) return { path: RoutePath.Goals };
    if (application.status === ApplicationStatusName.ManualReview) {
      if (application.crsNoFileReturned) {
        return { path: RoutePath.ExtendedIdentityCheck };
      }
      if (application.crsSmfaVerificationRequired) {
        return { path: RoutePath.BureauVerification };
      }
      if (application.frozenCreditScore) {
        return { path: RoutePath.UnfreezeCreditScore };
      }
      return { path: RoutePath.OfferStatus };
    }
    if (application.status === ApplicationStatusName.ManualVerify && application.kycPending) {
      if (application.kycStatus === KycStatus.PendingDocuments) {
        return { path: RoutePath.Alloy };
      }
      if (isManualReviewKycStatus(application.kycStatus)) {
        return { path: RoutePath.OfferStatus };
      }
      return { path: RoutePath.VerifyingKyc };
    }
    return { path: getPathByStatus(application.status, DC_STATUS_PATHS) };
  },
  [CurrentFlow.FinancialCheckup]: (application?: GetApplicationData) => ({
    path: application?.status ? getPathByStatus(application.status, FC_STATUS_PATHS) : RoutePath.Goals,
  }),
  [CurrentFlow.Card]: (application?: GetApplicationData, completedCustomize?: boolean) => {
    if (application?.status) return { path: RoutePath.CardApplicationSuccessful };
    return { path: completedCustomize ? RoutePath.YourPhoneNumber : RoutePath.YourName };
  },
  [CurrentFlow.StudentLoanForgiveness]: (application?: GetApplicationData) =>
    application?.status ? { path: RoutePath.StudentLoanEmployer } : { path: RoutePath.YourName },
  [CurrentFlow.MissedPayment]: () => ({ path: RoutePath.MissedPayment }),
  [CurrentFlow.AICoach]: (application?: GetApplicationData) => {
    if (!application?.status) return { path: RoutePath.AICoachGoals };
    if ([ApplicationStatusName.Rejected, ApplicationStatusName.OfferAvailable].includes(application.status)) {
      return { path: RoutePath.FinancialDashboard };
    }
    if (application.status === ApplicationStatusName.ManualReview) {
      if (application.crsNoFileReturned) {
        return { path: RoutePath.ExtendedIdentityCheck };
      }
      if (application.crsSmfaVerificationRequired) {
        return { path: RoutePath.BureauVerification };
      }
      if (application.frozenCreditScore) {
        return { path: RoutePath.UnfreezeCreditScore };
      }
    }
    return { path: getPathByStatus(application.status, AI_COACH_STATUS_PATHS) };
  },
};

interface ApplicationStep {
  stepName?: RoutePath;
  currentFlow: CurrentFlow;
  completedCustomize?: boolean;
}

const initialState: ApplicationStep = { currentFlow: CurrentFlow.AICoach };

const updateAnalytics = (state: ApplicationStep) => {
  const step = ApplicationStepName[state.stepName as RoutePath];

  if (!step && getEnvironment() === Environments.Staging) {
    return;
  }

  analytics.track(`${step} Step`);
  (window as any).nid('start', step);
};

const applicationStep = createSlice({
  name: 'applicationStep',
  initialState,
  reducers: {
    setApplicationStep: (state: ApplicationStep, { payload }: PayloadAction<RoutePath>) => {
      state.stepName = payload;
      updateAnalytics(state);
    },
    setApplicationFlow: (state: ApplicationStep, { payload }: PayloadAction<ApplicationStep>) => {
      state.currentFlow = payload.currentFlow ?? CurrentFlow.AICoach;
      state.completedCustomize = payload.completedCustomize;
      analytics.identify({ currentFlow: payload.currentFlow });
      state.stepName =
        payload.stepName ?? FLOW_TO_STEP_MAP[state.currentFlow!](undefined, payload.completedCustomize).path;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getApplicationData.fulfilled, (state, { payload }) => {
      state.currentFlow = payload.application.currentFlow ?? CurrentFlow.AICoach;
      state.stepName = FLOW_TO_STEP_MAP[state.currentFlow](payload.application).path;
    });

    builder.addCase(updateApplicationCurrentFlow.fulfilled, (state, { payload }) => {
      state.currentFlow = payload;
    });
  },
});

export const { setApplicationStep, setApplicationFlow } = applicationStep.actions;
export default applicationStep.reducer;
